




import { Component, Vue } from 'vue-property-decorator'
import { Scheduler, PresetManager } from "bryntum-scheduler"
import moment from 'moment'
import { GET_SELECTED_UI_LANGUAGE } from '@/store/getter-types'
import i18n from '@/i18n'

// Defines a Vue component that wraps Bryntum Scheduler
@Component({
  props : {
    // Configs
    locale: {
      default: 'de',
      type: String
    },
    autoHeight                     : Boolean,
    barMargin                      : Number,
    columns                        : Array,
    emptyText                      : String,
    eventBodyTemplate              : Function,
    eventColor: {
      default: 'green',
      type: String
    },
    eventLayout                    : String,
    eventRenderer                  : Function,
    eventStyle: {
      default: 'plain',
      type: String
    },
    endDate                        : Date,
    fillTicks                      : Boolean,
    forceFit                       : Boolean,
    hideHeaders                    : Boolean,
    id                             : String,
    milestoneCharWidth             : Number,
    milestoneLayoutMode            : String,
    multiEventSelect               : Boolean,
    readOnly                       : Boolean,
    responsiveLevels               : {
      default : undefined,
      type    : Object
    },
    rowHeight                      : {type: Number, default: 27},
    showRemoveEventInContextMenu   : Boolean,
    showRemoveRowInContextMenu     : Boolean,
    showUnassignEventInContextMenu : Boolean,
    startDate                      : Date,
    viewPreset                     : {
      default : "month",
      type    : [ String, Object ]
    },

    // Stores
    assignmentStore : Object,
    dependencyStore : Object,
    eventStore      : Object,
    resourceStore   : Object,
    equipmentStore  : Object,

    crudManager : Object,

    // Data
    assignments  : Array,
    dependencies : Array,
    events       : Array,
    resources    : Array,
    timeRanges   : Array,

    config : Object,

    // Features, only used for initialization
    cellEditFeature          : { type : [ Boolean, Object ], default : true },
    columnLinesFeature       : { type : Boolean, default : true },
    dependenciesFeature      : [ Boolean, Object ],
    eventContextMenuFeature  : { type : [ Boolean, Object ], default : true },
    eventDragCreateFeature   : { type : [ Boolean, Object ], default : true },
    eventDragFeature         : { type : [ Boolean, Object ], default : true },
    eventEditFeature         : { type : [ Boolean, Object ], default : false },
    eventFilterFeature       : { type : [ Boolean, Object ], default : true },
    eventResizeFeature       : { type : [ Boolean, Object ], default : true },
    eventTooltipFeature      : { type : [ Boolean, Object ], default : true },
    groupFeature             : [ Boolean, Object, String ],
    groupSummaryFeature      : [ Boolean, Object ],
    headerContextMenuFeature : { type : [ Boolean, Object ], default : true },
    labelsFeature            : [ Boolean, Object ],
    nonWorkingTimeFeature    : [ Boolean, Object ],
    regionResizeFeature      : Boolean,
    scheduleTooltipFeature   : { type : [ Boolean, Object ], default : true },
    sortFeature              : [ Boolean, Object, String, Array ],
    stripeFeature            : Boolean,
    summaryToolbarFeature    : [ Boolean, Object ],
    timeRangesFeature        : { type : [ Boolean, Object ], default : true },
    weekStartDay: { type: Number, default: 1 },
    managedEventSizing : { type: Boolean, default: false },
    transitionDuration : { type: Number, default: 0 },
    horizontalEventSorterFn: {type: Function}
  }
})
export default class SchedulerComponent extends Vue {
  public name = "scheduler";

  public schedulerEngine!: Scheduler;

  public mounted() {
    // Added to change the language of months
    const selectedLocale = this.$store.getters[GET_SELECTED_UI_LANGUAGE].code
    //let vm = this

    const year = {
      tickWidth: 50,       // Time column width
      //displayDateFormat: 'HH:mm',  // Controls how dates will be displayed in tooltips etc
      shiftIncrement: 1,        // Controls how much time to skip when calling shiftNext and shiftPrevious.
      shiftUnit: 'month',    // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
      //defaultSpan: 24,       // By default, if no end date is supplied to a view it will show 12 hours

      timeResolution: {         // Dates will be snapped to this resolution
        unit: 'minute',       // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
        increment: 15
      },

      headerConfig: {         // This defines your header, you must include a 'middle' object, and top/bottom are optional.
        bottom: {                    // For each row you can define 'unit', 'increment', 'dateFormat', 'renderer', 'align', and 'thisObj'
          unit: 'day',
          dateFormat: 'dd',
          renderer: function(start: string | Date){ // start, end, headerConfig, index
            return moment(start).locale(selectedLocale).format('DD') +' '+ moment(start).locale(selectedLocale).format('dd');  // changed call from vm.$props.locale to selectedLocale
          }
        },
        middle: {
          unit: 'month',
          dateFormat: 'MMMM',
          renderer: function (start: string | Date) {
            return moment(start).locale(selectedLocale).format('MMMM') +" "+ moment(start).locale(selectedLocale).format('YYYY');
          }
        },
        top: {
          unit: 'week',
          dateFormat: 'W',
          renderer: function (start: string | Date) {
            return i18n.t('calendarWeek').toString()+ ' '  + moment(start).locale(selectedLocale).format('W');
          }
        }
      },
      columnLinesFor: 'bottom'// Defines header level column lines will be drawn for

    }

    const month = {
      tickWidth: 35,       // Time column width
      //displayDateFormat: 'HH:mm',  // Controls how dates will be displayed in tooltips etc
      shiftIncrement: 1,        // Controls how much time to skip when calling shiftNext and shiftPrevious.
      shiftUnit: 'month',    // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
      //defaultSpan: 24,       // By default, if no end date is supplied to a view it will show 12 hours

      timeResolution: {         // Dates will be snapped to this resolution
        unit: 'minute',       // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
        increment: 15
      },

      headerConfig: {         // This defines your header, you must include a 'middle' object, and top/bottom are optional.
        bottom: {                    // For each row you can define 'unit', 'increment', 'dateFormat', 'renderer', 'align', and 'thisObj'
          unit: 'day',
          dateFormat: 'dd',
          renderer: function(start: string | Date){ // start, end, headerConfig, index
            return moment(start).locale(selectedLocale).format('dd');  // changed call from vm.$props.locale to selectedLocale
          }
        },
        middle: {
          unit: 'day',
          dateFormat: 'DD'
        },
        top: {
          unit: 'week',
          dateFormat: 'W',
          renderer: function (start: string | Date) {
            return i18n.t('calendarWeek').toString()+ ' ' + moment(start).locale(selectedLocale).format('W');
          }
        }
      },
      columnLinesFor: 'bottom'// Defines header level column lines will be drawn for

    }

    const week = {
      tickWidth: 60,       // Time column width
      //displayDateFormat: 'HH:mm',  // Controls how dates will be displayed in tooltips etc
      shiftIncrement: 1,        // Controls how much time to skip when calling shiftNext and shiftPrevious.
      shiftUnit: 'week',    // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
      //defaultSpan: 24,       // By default, if no end date is supplied to a view it will show 12 hours

      timeResolution: {         // Dates will be snapped to this resolution
        unit: 'minute',       // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
        increment: 15
      },

      headerConfig: {         // This defines your header, you must include a 'middle' object, and top/bottom are optional.
        bottom: {                    // For each row you can define 'unit', 'increment', 'dateFormat', 'renderer', 'align', and 'thisObj'
          unit: 'day',
          dateFormat: 'dd',
          renderer: function(start: string | Date){
            return moment(start).locale(selectedLocale).format('dd');  // changed call from vm.$props.locale to selectedLocale
          }
        },
        middle: {
          unit: 'day',
          dateFormat: 'DD'
        },
        top: {
          unit: 'week',
          dateFormat: 'W',
          renderer: function (start: string | Date) {
            return i18n.t('calendarWeek').toString()+ ' ' + moment(start).locale(selectedLocale).format('W');
          }
        }
      },
      columnLinesFor: 'bottom'// Defines header level column lines will be drawn for

    }

    const day = {
      tickWidth           : 44,       // Time column width

      displayDateFormat   : 'HH:mm',  // Controls how dates will be displayed in tooltips etc

      shiftIncrement      : 1,        // Controls how much time to skip when calling shiftNext and shiftPrevious.
      shiftUnit           : 'day',    // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
      defaultSpan         : 12,       // By default, if no end date is supplied to a view it will show 12 hours

      timeResolution      : {         // Dates will be snapped to this resolution
        unit        : 'minute',       // Valid values are 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'.
        increment   : 15
      },

      headerConfig        : {         // This defines your header, you must include a 'middle' object, and top/bottom are optional.
        middle : {                    // For each row you can define 'unit', 'increment', 'dateFormat', 'renderer', 'align', and 'thisObj'
          unit        : 'hour',
          dateFormat  : this.$vuetify.breakpoint.smOnly || this.$vuetify.breakpoint.xsOnly ? 'HH' : 'HH:mm',  // Add condition
        },
        top    : {
          unit        : 'day',
          dateFormat  : 'dddd DD MMM YYYY',
          renderer: function(start: string | Date, end: string | Date, headerConfig: any, index: any){
            return moment(start).locale(selectedLocale).format('dddd DD MMM YYYY');  // changed call from vm.$props.locale to selectedLocale
          }
        }
      },

      columnLinesFor      : 'middle'  // Defines header level column lines will be drawn for
    }

    PresetManager.registerPreset('year', year);
    PresetManager.registerPreset('week', week);
    PresetManager.registerPreset('month', month);
    PresetManager.registerPreset('day', day);

    const propKeys = Object.keys(this.$props);
    const featureConfig: Record<string, any> = {};

    const config: Record<string, any> = {
      // Render grid to components element
      appendTo : this.$el,

      // Listeners, will relay events using $emit
      listeners : {
        catchAll(event: any) {
          // Uncomment this line to log events being emitted to console
          this.$emit(event.type, event);
        },

        thisObj : this
      },

      features : featureConfig
    };

    // Apply all props to grid config
    propKeys.forEach((prop: any) => {
      if (prop.indexOf("Feature") > -1) {
        const featureName = prop.substr(0, prop.length - "Feature".length);
        // Prop is a feature config
        featureConfig[featureName] = this.$props[prop];
      } else if (prop === "config") {
        // Prop is a config object
        Object.assign(config, this.$props[prop]);
      } else {
        // Prop is a config
        if (this.$props[prop] !== undefined) config[prop] = this.$props[prop];

        // Set up a watcher
        this.$watch(prop, (newValue: any) => {
          // @ts-ignore
          this.schedulerEngine[prop] = Array.isArray(newValue) ? newValue.slice() : newValue;
        });
      }
    }, this);

    // Create a Bryntum Scheduler with props as configs
    const engine = this.schedulerEngine = new Scheduler(config);

    engine.eventStore && engine.eventStore.relayAll(engine, "eventStore");
    engine.resourceStore && engine.resourceStore.relayAll(engine, "resourceStore");
    engine.assignmentStore && engine.assignmentStore.relayAll(engine, "assignmentStore");
    engine.dependencyStore && engine.dependencyStore.relayAll(engine, "dependencyStore");
  }

  public beforeDestroy() {
    // Make sure Bryntum Grid is destroyed when vue component is
    this.schedulerEngine.destroy();
  }

  // methods : {
  public removeEvent() {
    const scheduler = this.schedulerEngine;
    scheduler.eventStore.remove(scheduler.selectedEvents);
  }

  public addEvent() {
    const scheduler = this.schedulerEngine,
      startDate = new Date(scheduler.startDate.getTime()),
      endDate = new Date(startDate.getTime());

    endDate.setHours(endDate.getHours() + 1);

    scheduler.eventStore.add({
      resourceId : scheduler.resourceStore.first.id,
      name       : "New task",
      desc       : "Meeting",
      startDate,
      endDate
    });
  }
}
